"use client";

import React, { useState, useCallback, useEffect, useRef } from "react";
import Label from "@/components/form/Label";

interface CurrencyFieldProps {
  id: string;
  name?: string;
  value: number | string;
  onChange: (value: number) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean | string;
  hint?: string | React.ReactNode;
  className?: string;
  inputClassName?: string;
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Show cents (2 decimal places). Default: true */
  showCents?: boolean;
  /** Currency symbol. Default: '$' */
  currencySymbol?: string;
}

/**
 * CurrencyField - Input field with auto-formatting for currency values
 * 
 * Features:
 * - Dollar sign prefix
 * - Auto-formats with thousand separators as you type (1,000.00)
 * - Limits to 2 decimal places
 * - Returns raw number value for form submission
 * - Smart cursor positioning after formatting
 * 
 * @example
 * <CurrencyField
 *   id="fee_amount"
 *   label="Fee Amount"
 *   value={formData.amount}
 *   onChange={(value) => setFormData(prev => ({ ...prev, amount: value }))}
 *   required
 *   hint="e.g., $500"
 * />
 */
const CurrencyField = React.forwardRef<HTMLInputElement, CurrencyFieldProps>(({
  id,
  name,
  value,
  onChange,
  onBlur,
  onKeyDown,
  label,
  placeholder = "0.00",
  required = false,
  error = false,
  hint,
  className = "",
  inputClassName = "",
  min = 0,
  max,
  disabled = false,
  showCents = true,
  currencySymbol = "$",
}, ref) => {
  const [displayValue, setDisplayValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPosRef = useRef<number>(0);
  const isFocusedRef = useRef(false);

  // Combine refs
  const setRefs = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  // Format number with thousand separators (preserving partial decimals while typing)
  const formatWithCommas = useCallback((str: string): string => {
    if (!str) return "";
    
    // Split into integer and decimal parts
    const parts = str.split('.');
    const integerPart = parts[0] || '';
    const decimalPart = parts[1];
    
    // Format integer part with commas
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Rebuild with decimal if present
    if (decimalPart !== undefined) {
      return `${formattedInteger}.${decimalPart}`;
    }
    return formattedInteger;
  }, []);

  // Parse string to number (removes formatting)
  const parseValue = useCallback((str: string): number => {
    // Remove all non-numeric characters except decimal point
    const cleaned = str.replace(/[^0-9.]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }, []);

  // Strip commas from string
  const stripCommas = useCallback((str: string): string => {
    return str.replace(/,/g, '');
  }, []);

  // Sync display value from prop only when not focused (avoids overwriting while user types)
  useEffect(() => {
    if (isFocusedRef.current) return;
    const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
    if (numValue === 0) {
      setDisplayValue("");
    } else {
      const formatted = showCents
        ? numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : numValue.toLocaleString('en-US');
      setDisplayValue(formatted);
    }
  }, [value, showCents]);

  // Handle input changes with live formatting
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPos = input.selectionStart || 0;
    
    // Allow empty input
    if (rawValue === '') {
      setDisplayValue('');
      onChange(0);
      return;
    }

    // Strip existing commas to get raw numbers
    const stripped = stripCommas(rawValue);
    
    // Only allow numbers and one decimal point
    let sanitized = stripped.replace(/[^0-9.]/g, '');
    
    // Handle multiple decimal points - keep only the first
    const dotIndex = sanitized.indexOf('.');
    if (dotIndex !== -1) {
      const beforeDot = sanitized.slice(0, dotIndex + 1);
      const afterDot = sanitized.slice(dotIndex + 1).replace(/\./g, '');
      // Limit decimal places to 2
      sanitized = beforeDot + afterDot.slice(0, 2);
    }

    // Format with commas
    const formatted = formatWithCommas(sanitized);
    
    // Calculate new cursor position
    // Count commas before cursor in old value
    const oldCommasBefore = (rawValue.slice(0, cursorPos).match(/,/g) || []).length;
    // Count commas before equivalent position in new value
    const strippedCursorPos = cursorPos - oldCommasBefore;
    const newCommasBefore = (formatted.slice(0, strippedCursorPos + 10).match(/,/g) || [])
      .filter((_, i) => {
        // Count only commas that would be before our target position
        let commaCount = 0;
        let charCount = 0;
        for (let j = 0; j < formatted.length && charCount <= strippedCursorPos; j++) {
          if (formatted[j] === ',') {
            if (charCount < strippedCursorPos) commaCount++;
          } else {
            charCount++;
          }
        }
        return i < commaCount;
      }).length;
    
    // Simple approach: count non-comma chars before cursor in stripped, add commas
    let newCursorPos = 0;
    let nonCommaCount = 0;
    const targetNonComma = Math.min(strippedCursorPos, sanitized.length);
    for (let i = 0; i < formatted.length && nonCommaCount < targetNonComma; i++) {
      newCursorPos = i + 1;
      if (formatted[i] !== ',') {
        nonCommaCount++;
      }
    }
    
    cursorPosRef.current = newCursorPos;
    setDisplayValue(formatted);
    
    // Parse and apply constraints
    const numValue = parseValue(sanitized);
    let constrainedValue = numValue;
    if (max !== undefined && numValue > max) constrainedValue = max;
    
    onChange(constrainedValue);
    
    // Set cursor position after React updates the DOM
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosRef.current, cursorPosRef.current);
      }
    });
  };

  // Handle focus - select all for easy replacement
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = true;
    setTimeout(() => {
      e.target.select();
    }, 0);
  };

  // Handle blur - ensure proper formatting with decimal places
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;
    const numValue = parseValue(displayValue);
    
    if (numValue === 0) {
      setDisplayValue("");
    } else if (showCents) {
      // Format with exactly 2 decimal places
      const formatted = numValue.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
      setDisplayValue(formatted);
    }
    
    // Apply min constraint on blur
    let finalValue = numValue;
    if (min !== undefined && numValue < min && numValue !== 0) {
      finalValue = min;
      const formatted = finalValue.toLocaleString('en-US', { 
        minimumFractionDigits: showCents ? 2 : 0, 
        maximumFractionDigits: showCents ? 2 : 0 
      });
      setDisplayValue(formatted);
    }
    
    onChange(finalValue);
    onBlur?.(e);
  };

  // Build input classes using TailAdmin styling
  const getInputClasses = () => {
    let baseClasses = "h-11 w-full rounded-lg border appearance-none pl-8 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30";
    
    if (disabled) {
      return `${baseClasses} text-gray-500 border-gray-300 opacity-40 bg-gray-100 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700`;
    } else if (error) {
      return `${baseClasses} border-error-500 focus:border-error-300 focus:ring-error-500/20 dark:text-error-400 dark:border-error-500 dark:focus:border-error-800`;
    } else {
      return `${baseClasses} bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800`;
    }
  };

  return (
    <div className={`${className ? `${className} w-full` : "w-full"} overflow-visible`}>
      {label && (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      )}
      <div className="relative">
        {/* Currency symbol */}
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none z-10">
          {currencySymbol}
        </span>
        
        <input
          ref={setRefs}
          id={id}
          name={name || id}
          type="text"
          inputMode="decimal"
          placeholder={placeholder}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          required={required}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-describedby={hint || (typeof error === 'string' && error) ? `${id}-message` : undefined}
          className={`${getInputClasses()} ${inputClassName}`.trim()}
        />
      </div>
      {(hint || (typeof error === 'string' && error)) && (
        <p
          id={`${id}-message`}
          role={typeof error === 'string' && error ? 'alert' : undefined}
          className={`mt-1.5 text-xs ${
            error
              ? "text-error-500"
              : "text-gray-500"
          }`}
        >
          {typeof error === 'string' ? error : hint}
        </p>
      )}
    </div>
  );
});

CurrencyField.displayName = 'CurrencyField';

export default CurrencyField;












